import { useMemo } from 'react'

import { useMemoizedFn } from 'ahooks'

import { TemplateVariableEntity } from '@eastcoal/api/contract/interface'

import { array2Map, toArray } from '@eastcoal/common/utils/array'
import { excludeNilFilter } from '@eastcoal/common/utils/lambda'
import { pick } from '@eastcoal/common/utils/object'

import { $getNodeByKey, NodeKey } from '@eastcoal/editor/exports'
import { useEditor } from '@eastcoal/editor/hooks/useEditor'

import { $isTableVariableNode } from '../../../editor/nodes/TableVariableNode'
import { $isTextVariableNode } from '../../../editor/nodes/TextVariableNode'
import { useContractDesignerStore } from '../../Store/hooks/useStore'

export const useTemplateVariableEntityGetter = () => {
  const editor = useEditor()
  const { templateVariableData } = useContractDesignerStore(state =>
    pick(state, ['templateVariableData'])
  )
  const templateVariableMap = useMemo(() => {
    return array2Map(templateVariableData, 'id')
  }, [templateVariableData])

  return useMemoizedFn((nodeKey?: NodeKey | NodeKey[]) => {
    if (!nodeKey) {
      return undefined as any
    }
    const nodeKeys = toArray(nodeKey)
    const result = editor.getEditorState().read(() => {
      return nodeKeys
        .map(nodeKey => {
          const node = $getNodeByKey(nodeKey)
          if (!($isTextVariableNode(node) || $isTableVariableNode(node))) {
            return undefined
          }
          const templateVariableId = node.getVariable().id
          return templateVariableMap[`${templateVariableId}`]
        })
        .filter(excludeNilFilter)
    })
    return (Array.isArray(nodeKey) ? result : result[0]) as any
  }) as <T extends NodeKey | NodeKey[]>(
    nodeKey?: T
  ) => T extends NodeKey | undefined
    ? TemplateVariableEntity | undefined
    : TemplateVariableEntity[]
}
